import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "../../i18n";
import "./RateAgreementSubForm.scss";

import "react-datepicker/dist/react-datepicker-min.module.css";

import { UseFormReturn } from "react-hook-form";
import { Panel } from "spot-component-library/lib/components/basic/panel/Panel";
import { Spinner } from "spot-component-library/lib/components/basic/spinner/Spinner";
import { Dropdown, DropdownResult } from "spot-component-library/lib/components/dropdown/Dropdown";
import { DropdownItem } from "spot-component-library/lib/components/dropdown/DropdownItem";
import { DateField } from "spot-component-library/lib/components/form/dateField/DateField";
import { SPOTRadioButton } from "spot-component-library/lib/components/form/radioButton/SPOTRadioButton";
import { SPOTInput } from "spot-component-library/lib/components/form/spotInput/SPOTInput";
import rateAgreementService from "../services/RateAgreementService";
import { RateAgreementFormData } from "../types/RateAgreementFormData";
import { RateAgreementQuestionType } from "../types/RateAgreementQuestionType";
import { RateAgreementStateInfo } from "../types/RateAgreementStateInfo";
import { RateAgreementInfoPanel } from "./RateAgreementInfoPanel";

type RateAgreementSubFormProps = {
    validatedDebtNumber?: string;
    onChange: (e: RateAgreementFormData) => void;
    form: UseFormReturn;
};

const changeTypeDropdownItems = [
    { value: "ONE_TIME_CHANGE", label: "RATE_AGREEMENT_SUB_FORM.FIELDS.CHANGE_TYPE.OPTIONS.ONE_TIME_CHANGE" },
    { value: "PERMANENT_CHANGE", label: "RATE_AGREEMENT_SUB_FORM.FIELDS.CHANGE_TYPE.OPTIONS.PERMANENT_CHANGE" },
];

export const RateAgreementSubForm = (props: RateAgreementSubFormProps) => {
    const { t } = useTranslation();
    const [rateAgreementFormData, setRateAgreementFormData] = useState<RateAgreementFormData>({});
    const [isRateAgreementPossible, setIsRateAgreementPossible] = useState<boolean>(false);
    const [activeRateAgreementExists, setActiveRateAgreementExists] = useState<boolean>(false);
    const [loadingRateAgreementInfo, setLoadingRateAgreementInfo] = useState<boolean>(false);
    const [showRateAgreementSpecificFields, setShowRateAgreementSpecificFields] = useState(false);

    React.useEffect(() => {
        setLoadingRateAgreementInfo(true);
        rateAgreementService
            .getRateAgreementInformation()
            .then((response: RateAgreementStateInfo) => {
                setIsRateAgreementPossible(response.isRateAgreementPossible);
                setActiveRateAgreementExists(response.hasActiveRateAgreement);
            })
            .finally(() => setLoadingRateAgreementInfo(false));
    }, [props.validatedDebtNumber]);

    const handleRadioButtonChange = ({ target: activeRadioBtn }: React.ChangeEvent<HTMLInputElement>) => {
        const rateAgreementFormTmp: RateAgreementFormData = { ...rateAgreementFormData };
        rateAgreementFormTmp.questionType = activeRadioBtn.value as RateAgreementQuestionType;
        setRateAgreementFormData(rateAgreementFormTmp);
        setShowRateAgreementSpecificFields(activeRadioBtn.value === RateAgreementQuestionType.EDIT_EXISTING_RATE_AGREEMENT);

        props.onChange(rateAgreementFormTmp);
    };
    const groupName = "rate-agreement-options";

    const handleFormOnChange = async (e: any) => {
        const target = e.target;
        const value = target.value;

        const rateAgreementFormTmp: any = { ...rateAgreementFormData };
        rateAgreementFormTmp[target.name] = value;
        setRateAgreementFormData(rateAgreementFormTmp);

        props.onChange(rateAgreementFormTmp);
    };

    const handleDropdownOnChange = async (dropDownResult: DropdownResult) => {
        const newRateAgreementFormData = {
            ...rateAgreementFormData,
            [dropDownResult.name]: dropDownResult.result.value,
        };

        props.form.setValue(dropDownResult.name, dropDownResult.result.value, { shouldValidate: true });
        setRateAgreementFormData(newRateAgreementFormData);

        props.onChange(newRateAgreementFormData);
    };

    const handleDateOnChange = async (d: Date) => {
        const rateAgreementFormTmp: RateAgreementFormData = { ...rateAgreementFormData };
        rateAgreementFormTmp.newPaymentDate = d;
        setRateAgreementFormData(rateAgreementFormTmp);

        props.onChange(rateAgreementFormTmp);
    };

    function addDays(date: Date, days: number): Date {
        const result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    }

    return (
        <div id={"rate-agreement-sub-form"}>
            {loadingRateAgreementInfo ? (
                <Spinner isLoading={loadingRateAgreementInfo} type={"page"} />
            ) : (
                <RateAgreementInfoPanel isRateAgreementPossible={isRateAgreementPossible} debtId={props.validatedDebtNumber} />
            )}
            <Panel>
                {activeRateAgreementExists && (
                    <SPOTRadioButton
                        id={"radio-btn-edit-rate-agreement"}
                        testId={"radio-btn-edit-rate-agreement"}
                        groupName={groupName}
                        checked={rateAgreementFormData.questionType === RateAgreementQuestionType.EDIT_EXISTING_RATE_AGREEMENT}
                        onChange={handleRadioButtonChange}
                        value={RateAgreementQuestionType.EDIT_EXISTING_RATE_AGREEMENT}
                        elementLabel={t("RATE_AGREEMENT_SUB_FORM.OPTIONS.EDIT_EXISTING_RATE_AGREEMENT")}
                    />
                )}
                <SPOTRadioButton
                    id={"radio-btn-further-questions"}
                    groupName={groupName}
                    checked={rateAgreementFormData.questionType === RateAgreementQuestionType.FURTHER_QUESTIONS}
                    onChange={handleRadioButtonChange}
                    value={RateAgreementQuestionType.FURTHER_QUESTIONS}
                    elementLabel={t("RATE_AGREEMENT_SUB_FORM.OPTIONS.I_HAVE_FURTHER_QUESTIONS")}
                />
                {showRateAgreementSpecificFields && (
                    <div className={"rate-agreement-input-fields"}>
                        <SPOTInput
                            id={"new-rate-amount"}
                            name={"newRateAmount"}
                            label={t("RATE_AGREEMENT_SUB_FORM.FIELDS.NEW_AMOUNT.LABEL")}
                            placeholder={t("RATE_AGREEMENT_SUB_FORM.FIELDS.NEW_AMOUNT.PLACEHOLDER")}
                            onChange={handleFormOnChange}
                            maxLength={10}
                            required={false}
                            pattern={{
                                value: /^\+?[1-9]\d*$/, // nur positive ganze Zahlen
                                message: t("RATE_AGREEMENT_SUB_FORM.FIELDS.NEW_AMOUNT.VALIDATION_ERROR"),
                            }}
                            register={props.form.register as any}
                            errors={props.form.formState.errors}
                            defaultValue={""}
                        />
                        <DateField
                            id={"new-payment-date"}
                            name={"newPaymentDate"}
                            label={t("RATE_AGREEMENT_SUB_FORM.FIELDS.PAYDAY.LABEL")}
                            placeholder={t("RATE_AGREEMENT_SUB_FORM.FIELDS.PAYDAY.PLACEHOLDER")}
                            onChange={(date: Date) => handleDateOnChange(date)}
                            value={rateAgreementFormData.newPaymentDate}
                            control={props.form.control as any}
                            errors={props.form.formState.errors}
                            required={false}
                            weekendsDisabled={true}
                            minDate={addDays(new Date(), 4)}
                            maxDate={addDays(new Date(), 35)}
                        />
                        <label className="input-field-label">
                            <b>{t("RATE_AGREEMENT_SUB_FORM.FIELDS.CHANGE_TYPE.LABEL")}*</b>
                        </label>
                        <Dropdown
                            initialSelection={changeTypeDropdownItems.findIndex((i) => i.value === rateAgreementFormData.changeType)}
                            placeholder={t("RATE_AGREEMENT_SUB_FORM.FIELDS.CHANGE_TYPE.PLACEHOLDER")}
                            onChange={handleDropdownOnChange}
                            withBorder={true}
                            register={props.form.register as any}
                            required={true}
                            dataTest={"changeType"}
                            name={"changeType"}>
                            {changeTypeDropdownItems.map((item) => (
                                <DropdownItem label={t(item.label)} value={item.value} key={item.value} />
                            ))}
                        </Dropdown>
                    </div>
                )}
            </Panel>
        </div>
    );
};
